export interface Point {
  lat: number;
  lng: number;
}

/**
 * Interpolates a single point along a Catmull-Rom spline at a fractional index.
 * For example, fractionalIndex 2.3 means 30% of the way between points[2] and
 * points[3], smoothed by the surrounding control points.
 */
export function catmullRomInterpolate(points: Point[], fractionalIndex: number): Point {
  if (points.length === 0) return { lat: 0, lng: 0 };
  if (points.length === 1) return points[0];

  const clamped = Math.max(0, Math.min(fractionalIndex, points.length - 1));
  const i = Math.floor(clamped);

  if (i >= points.length - 1) return points[points.length - 1];
  if (points.length < 3) {
    const t = clamped - i;
    return {
      lat: points[i].lat + (points[i + 1].lat - points[i].lat) * t,
      lng: points[i].lng + (points[i + 1].lng - points[i].lng) * t,
    };
  }

  const p0 = points[Math.max(i - 1, 0)];
  const p1 = points[i];
  const p2 = points[i + 1];
  const p3 = points[Math.min(i + 2, points.length - 1)];
  const t = clamped - i;
  const t2 = t * t;
  const t3 = t2 * t;

  return {
    lat:
      0.5 *
      (2 * p1.lat +
        (-p0.lat + p2.lat) * t +
        (2 * p0.lat - 5 * p1.lat + 4 * p2.lat - p3.lat) * t2 +
        (-p0.lat + 3 * p1.lat - 3 * p2.lat + p3.lat) * t3),
    lng:
      0.5 *
      (2 * p1.lng +
        (-p0.lng + p2.lng) * t +
        (2 * p0.lng - 5 * p1.lng + 4 * p2.lng - p3.lng) * t2 +
        (-p0.lng + 3 * p1.lng - 3 * p2.lng + p3.lng) * t3),
  };
}

// how far ahead/behind (in fractional-index units) to sample when computing
// the smoothed heading — larger = more filtering, less responsive to turns
const BEARING_SMOOTHING = 0.3;

/**
 * Computes the bearing at a fractional index by sampling two positions on the
 * Catmull-Rom spline slightly before and after the target point. This gives a
 * heading that's naturally smoothed over a small window, avoiding abrupt
 * rotations from noisy GPS data.
 *
 * Device-reported bearing_degrees is intentionally ignored because GPS devices
 * frequently report stale values that lag behind turns.
 */
export function interpolateBearing(points: Point[], fractionalIndex: number): number {
  if (points.length < 2) return 0;

  const maxIdx = points.length - 1;
  const before = catmullRomInterpolate(
    points,
    Math.max(0, fractionalIndex - BEARING_SMOOTHING),
  );
  const after = catmullRomInterpolate(
    points,
    Math.min(maxIdx, fractionalIndex + BEARING_SMOOTHING),
  );

  return vectorToHeading(after.lat - before.lat, after.lng - before.lng);
}

/** converts a (dLat, dLng) vector to a compass heading (0=north, clockwise). */
export function vectorToHeading(dLat: number, dLng: number): number {
  const angle = (Math.atan2(dLng, dLat) * 180) / Math.PI;
  return ((angle % 360) + 360) % 360;
}

// ---------------------------------------------------------------------------
// time-based interpolation
// ---------------------------------------------------------------------------

export interface TimedPath {
  rawPath: Point[];
  pingTimes: number[];
}

/**
 * Given a path with timestamps and a target time, computes the interpolated
 * position and bearing on the Catmull-Rom spline. Returns null if the time
 * falls outside the path's time range.
 */
export function interpolatePositionAtTime(
  path: TimedPath,
  time: number,
): { position: Point; bearing: number } | null {
  const { rawPath, pingTimes } = path;
  if (rawPath.length === 0) return null;

  if (time < pingTimes[0] || time > pingTimes[pingTimes.length - 1]) {
    return null;
  }

  // binary search — sortedIndex is not available here so we inline it
  let lo = 0;
  let hi = pingTimes.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (pingTimes[mid] < time) lo = mid + 1;
    else hi = mid;
  }
  const idx = lo;

  let fractionalIdx: number;
  if (idx === 0) {
    fractionalIdx = 0;
  } else if (idx >= pingTimes.length) {
    fractionalIdx = pingTimes.length - 1;
  } else {
    const before = pingTimes[idx - 1];
    const after = pingTimes[idx];
    const fraction = after === before ? 0 : (time - before) / (after - before);
    fractionalIdx = idx - 1 + fraction;
  }

  const bearing = interpolateBearing(rawPath, fractionalIdx);
  const position =
    rawPath.length < 2
      ? (rawPath[Math.round(fractionalIdx)] ?? rawPath[0])
      : catmullRomInterpolate(rawPath, fractionalIdx);

  return { position, bearing };
}

/**
 * Builds a TimedPath from an array of location pings, filtering out invalid
 * coordinates (near 0,0).
 */
export function buildTimedPath(
  pings: { lat: string; lon: string; occurredAt: { valueOf(): number } }[],
): TimedPath {
  const valid = pings.filter((p) => {
    const lat = parseFloat(p.lat);
    const lng = parseFloat(p.lon);
    return Math.abs(lat) > 0.01 || Math.abs(lng) > 0.01;
  });
  return {
    rawPath: valid.map((p) => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lon) })),
    pingTimes: valid.map((p) => p.occurredAt.valueOf()),
  };
}
